import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, mxn, fmtDate } from "@/lib/api";
import { DocumentSheet } from "@/components/DocumentSheet";
import { DocDetailHeader } from "@/components/DocDetailHeader";
import { PrintRemisionDialog } from "@/components/PrintRemisionDialog";
import { StatusBadge } from "@/components/StatusBadge";
import { ConfirmDialog } from "@/components/Kit";
import { Printer, Download, Copy, Pencil, Trash2 } from "lucide-react";

const TITLES = { cotizacion: "Cotización", remision: "Remisión", orden: "Orden de trabajo", factura: "Factura" };
const ENDPOINTS = { cotizacion: "quotes", remision: "remisiones", orden: "work-orders", factura: "invoices" };

export default function DocumentoDetalle() {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [company, setCompany] = useState({ name: "TAS Publicidad 360" });
  const [tab, setTab] = useState("general");
  const [printOpen, setPrintOpen] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  const LIST_PATH = { cotizacion: "/cotizaciones", remision: "/remisiones", orden: "/ordenes", factura: "/facturas" };

  const isInvoice = type === "factura";
  const isQuote = type === "cotizacion";

  useEffect(() => {
    const path = ENDPOINTS[type] || "invoices";
    api.get(`/${path}/${id}`).then((r) => setDoc(r.data)).catch(() => setDoc(false));
    api.get("/settings").then((r) => setCompany(r.data.company || {})).catch(() => {});
  }, [type, id]);

  useEffect(() => {
    document.body.style.background = "#EEF2F6";
    return () => (document.body.style.background = "");
  }, []);

  const handlePrint = () => {
    if (type === "remision") setPrintOpen(true);
    else window.open(`/imprimir/${type}/${id}`, "_blank");
  };

  const doDelete = async () => {
    try {
      await api.delete(`/${ENDPOINTS[type] || "invoices"}/${id}`);
      toast.success("Documento eliminado");
      navigate(LIST_PATH[type] || "/");
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  if (doc === null) return <div className="p-10 font-mono-data text-[#5B6672]">Cargando…</div>;
  if (doc === false) return <div className="p-10 font-mono-data text-[#ED1F26]">Documento no encontrado.</div>;

  const c = doc.client || {};
  const totalUnits = (doc.items || []).reduce((s, it) => s + (Number(it.quantity) || 0), 0);
  const payments = doc.payments || [];

  const listPath = LIST_PATH[type] || "/";
  const canDuplicate = type === "cotizacion" || type === "orden";
  const actions = [
    { label: "Imprimir", icon: <Printer size={16} />, onClick: handlePrint, testid: "action-imprimir" },
    { label: "Descargar PDF", icon: <Download size={16} />, onClick: handlePrint, testid: "action-descargar" },
    ...(canDuplicate ? [{ label: "Duplicar", icon: <Copy size={16} />, onClick: () => navigate(`${listPath}?duplicate=${id}`), testid: "action-duplicar" }] : []),
    { label: "Editar", icon: <Pencil size={16} />, onClick: () => navigate(`${listPath}?edit=${id}`), testid: "action-editar" },
    { label: "Eliminar", icon: <Trash2 size={16} />, onClick: () => setConfirmDel(true), danger: true, testid: "action-eliminar" },
  ];

  return (
    <div className="min-h-screen data-emphasis" data-testid="documento-detalle">
      <DocDetailHeader
        title={`${TITLES[type]} ${doc.number}`}
        subtitle={c.name || "—"}
        onClose={() => navigate(-1)}
        actions={actions}
      />

      <div className="grid lg:grid-cols-[1fr_380px]">
        {/* Document preview */}
        <div className="p-6 lg:p-10 overflow-auto">
          <DocumentSheet doc={doc} company={company} type={type} variant="screen" />
        </div>

        {/* Right panel */}
        <div className="border-l border-[#E3E8EE] bg-white min-h-[calc(100vh-4rem)]">
          <div className="flex border-b border-[#E3E8EE]">
            <TabBtn active={tab === "general"} onClick={() => setTab("general")} testid="detalle-tab-general">General</TabBtn>
            <TabBtn active={tab === "historial"} onClick={() => setTab("historial")} testid="detalle-tab-historial">Historial</TabBtn>
          </div>

          {tab === "general" && (
            <div className="p-6 space-y-6">
              <div className="flex items-center justify-between">
                <span className="overline">Total</span>
                <span className="font-mono-data text-xl font-bold text-[#16202B]" data-testid="detalle-total">{mxn(doc.total)}</span>
              </div>

              <div className="space-y-3 border-t border-[#E3E8EE] pt-4">
                <Field label="Número de documento" value={doc.number} />
                <Field label="Contacto" value={c.name || "—"} />
                {type === "orden" && <Field label="Remisión" value={doc.remision_number || "—"} testid="doc-remision-number" />}
                {isQuote && <Field label="Remisión relacionada" value={doc.remision_numbers && doc.remision_numbers.length ? doc.remision_numbers.join(", ") : "—"} testid="doc-quote-remisiones" />}
                <Field label="Fecha" value={fmtDate(doc.created_at)} />
                {isQuote && doc.valid_until && <Field label="Válida hasta" value={fmtDate(doc.valid_until)} />}
                {!isQuote && doc.due_date && <Field label="Vencimiento" value={fmtDate(doc.due_date)} />}
                <Field label="Total unidades" value={totalUnits} />
                <div className="flex items-center justify-between">
                  <span className="font-mono-data text-sm text-[#5B6672]">Estatus</span>
                  <StatusBadge status={doc.status} />
                </div>
              </div>

              {isInvoice && (
                <div className="border-t border-[#E3E8EE] pt-4">
                  <div className="overline mb-3">Pagos</div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-mono-data text-sm text-[#5B6672]">
                      {doc.balance > 0 ? "Pendiente de pago" : "Pagado"}
                    </span>
                    <span className={`font-mono-data text-sm font-semibold ${doc.balance > 0 ? "text-[#ED1F26]" : "text-[#49AC9A]"}`}>
                      {mxn(doc.balance > 0 ? doc.balance : doc.total)}
                    </span>
                  </div>
                  {payments.length === 0 ? (
                    <p className="font-mono-data text-xs text-[#5B6672]">Sin pagos registrados.</p>
                  ) : (
                    <div className="space-y-2">
                      {payments.map((p) => (
                        <div key={p.id} className="flex items-center justify-between font-mono-data text-xs" data-testid={`detalle-payment-${p.id}`}>
                          <span className="text-[#5B6672]">
                            {fmtDate(p.date)} · {p.method || "—"}
                            {p.bank ? ` · ${p.bank}` : ""}
                            {p.reference ? ` · Ref. ${p.reference}` : ""}
                          </span>
                          <span className="text-[#16202B]">{mxn(p.amount)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {doc.balance > 0 && (
                    <button
                      onClick={() => navigate("/cobranza")}
                      data-testid="detalle-add-payment"
                      className="mt-4 text-sm text-[#465B7F] hover:underline"
                    >
                      Registrar pago en Cobranza →
                    </button>
                  )}
                </div>
              )}

              {doc.notes && (
                <div className="border-t border-[#E3E8EE] pt-4">
                  <div className="overline mb-2">Notas</div>
                  <p className="font-mono-data text-sm text-[#16202B]">{doc.notes}</p>
                </div>
              )}
            </div>
          )}

          {tab === "historial" && (
            <div className="p-6 space-y-4">
              <HistItem label="Creado" value={fmtDate(doc.created_at)} />
              {doc.authorized_by && (
                <HistItem label="Autorizado por" value={`${doc.authorized_by}${doc.authorized_at ? " · " + fmtDate(doc.authorized_at) : ""}`} />
              )}
              {doc.invoiced && <HistItem label="Facturado" value="Sí" />}
            </div>
          )}
        </div>
      </div>

      <PrintRemisionDialog open={printOpen} onClose={() => setPrintOpen(false)} remisionId={id} />
      <ConfirmDialog
        open={confirmDel}
        onClose={() => setConfirmDel(false)}
        onConfirm={doDelete}
        title={`Eliminar ${(TITLES[type] || "documento").toLowerCase()}`}
        message={`¿Eliminar ${(TITLES[type] || "documento").toLowerCase()} ${doc.number}? Esta acción no se puede deshacer.`}
        testid="confirm-delete-documento"
      />
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex-1 py-3 text-sm uppercase tracking-widest transition-colors ${
        active ? "bg-[#16202B] text-white" : "text-[#5B6672] hover:bg-[#E9EEF3]"
      }`}
    >
      {children}
    </button>
  );
}

function Field({ label, value, testid }) {
  return (
    <div className="flex items-center justify-between gap-4" data-testid={testid}>
      <span className="font-mono-data text-sm text-[#5B6672]">{label}</span>
      <span className="font-mono-data text-sm text-[#16202B] text-right">{value}</span>
    </div>
  );
}

function HistItem({ label, value }) {
  return (
    <div>
      <div className="overline">{label}</div>
      <div className="font-mono-data text-sm text-[#16202B] mt-1">{value}</div>
    </div>
  );
}
